<template>
  <!-- MODO HORIZONTAL (menú desktop) -->
  <div
    v-if="item && orientation === 'horizontal' && !forceVertical"
    class="relative flex-shrink-0 menu-node-item"
    :data-id="item.id"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <button
      @click.stop="handleClick"
      class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] transition-all whitespace-nowrap flex-shrink-0 focus:outline-none cursor-pointer select-none"
      :class="[
        isAnyActive(rutasSubarbol)
          ? 'bg-indigo-600 text-white dark:bg-indigo-600 dark:text-white font-bold shadow-sm'
          : tieneHijos
            ? (abierto ? 'bg-indigo-100 dark:bg-gray-700 text-indigo-700 dark:text-indigo-300 font-bold' : 'text-gray-900 dark:text-white font-bold hover:bg-indigo-50 dark:hover:bg-gray-700')
            : 'text-gray-700 dark:text-gray-200 font-medium hover:bg-gray-100 dark:hover:bg-gray-700'
      ]"
    >
      <Icon
        :icon="item.icono || 'mdi:folder'"
        width="16"
        height="16"
        :class="tieneHijos ? (abierto ? 'text-indigo-700 dark:text-indigo-300' : 'text-indigo-600 dark:text-indigo-400') : 'text-gray-500 dark:text-gray-400'"
      />
      <span>{{ item.nombre }}</span>
      <Icon
        v-if="tieneHijos"
        :icon="abierto ? 'mdi:chevron-up' : 'mdi:chevron-down'"
        width="14"
        height="14"
        class="text-indigo-600 dark:text-indigo-400 flex-shrink-0 transition-transform duration-200"
      />
    </button>

    <!-- Dropdown con puente invisible para no perder hover -->
    <transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 -translate-y-1 scale-95"
    >
      <div
        v-if="tieneHijos && abierto"
        class="absolute top-full pt-1 z-[9999]"
        :class="alineacionDerecha ? 'right-0' : 'left-0'"
        @mouseenter="cancelarCierre"
        @mouseleave="onMouseLeave"
      >
        <div class="min-w-[250px] max-w-[340px] max-h-[80vh] overflow-y-auto bg-white dark:bg-gray-900 rounded-xl shadow-2xl border-2 border-indigo-200 dark:border-indigo-500/50 py-1.5 custom-menu-scroll">
          <SubMenuNode
            v-for="hijo in item.hijos"
            :key="hijo.id"
            :item="hijo"
            :nivel="1"
          />
        </div>
      </div>
    </transition>
  </div>

  <!-- MODO VERTICAL (drawer móvil o menú Más) -->
  <template v-else-if="item">
    <CollapsibleSection
      v-if="tieneHijos"
      :title="item.nombre"
      :icon="item.icono || 'mdi:folder'"
      :sidebar-collapsed="false"
      :initial-open="debeAbrirInicial"
      :name="`section-L${level}-${item.id}`"
      :active-section="activeSection"
      :level="level"
      @section-opened="$emit('section-opened', $event)"
    >
      <MenuNode
        v-for="hijo in item.hijos"
        :key="hijo.id"
        :item="hijo"
        :collapsed="false"
        :level="level + 1"
        :active-section="activeSection"
        orientation="vertical"
        @item-click="$emit('item-click', $event)"
        @section-opened="$emit('section-opened', $event)"
      />
    </CollapsibleSection>

    <MenuItem
      v-else
      :to="item.ruta || '#'"
      :icon="item.icono || 'mdi:circle-small'"
      :title="item.nombre"
      :collapsed="false"
      :is-active="isActive(item.ruta)"
      :level="level"
      @click="$emit('item-click', item)"
      class="mb-1"
    />
  </template>
</template>

<script>
import { Icon } from '@iconify/vue';
import MenuItem from './MenuItem.vue';
import CollapsibleSection from './CollapsibleSection.vue';
import SubMenuNode from './SubMenuNode.vue';

export default {
  name: 'MenuNode',
  components: { Icon, MenuItem, CollapsibleSection, SubMenuNode },
  emits: ['item-click', 'section-opened'],
  props: {
    item:          { type: Object,  required: false, default: null },
    collapsed:     { type: Boolean, default: false },
    level:         { type: Number,  default: 0 },
    activeSection: { type: String,  default: null },
    orientation:   { type: String,  default: 'vertical' },
    forceVertical: { type: Boolean, default: false }
  },
  data() {
    return {
      abierto: false,
      hoverTimeout: null,
      alineacionDerecha: false
    };
  },
  computed: {
    tieneHijos() {
      return Array.isArray(this.item?.hijos) && this.item.hijos.length > 0;
    },
    rutasSubarbol() {
      if (!this.item) return [];
      const rutas = [];
      const stack = [this.item];
      while (stack.length) {
        const actual = stack.pop();
        if (!actual) continue;
        if (actual.ruta) rutas.push(actual.ruta);
        if (Array.isArray(actual.hijos)) stack.push(...actual.hijos);
      }
      return rutas;
    },
    debeAbrirInicial() {
      if (!this.item) return false;
      return this.isAnyActive(this.rutasSubarbol);
    }
  },
  mounted() {
    document.addEventListener('click', this.cerrarSiClickFuera);
  },
  beforeUnmount() {
    this.cancelarCierre();
    document.removeEventListener('click', this.cerrarSiClickFuera);
  },
  methods: {
    calcularAlineacion() {
      if (!this.$el) return;
      try {
        const rect = this.$el.getBoundingClientRect();
        // Si desde el borde izquierdo del elemento + 270px se sale de la pantalla, alinear a la derecha
        this.alineacionDerecha = (rect.left + 270 > window.innerWidth);
      } catch {
        this.alineacionDerecha = false;
      }
    },
    onMouseEnter() {
      if (!this.tieneHijos) return;
      this.cancelarCierre();
      this.calcularAlineacion();
      this.abierto = true;
    },
    onMouseLeave() {
      this.cancelarCierre();
      // Pequeño margen de 220ms para que nunca se cierre accidentalmente al mover el cursor
      this.hoverTimeout = setTimeout(() => {
        this.abierto = false;
      }, 220);
    },
    cancelarCierre() {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }
    },
    cerrarSiClickFuera(e) {
      if (!this.abierto) return;
      if (this.$el && !this.$el.contains(e.target)) {
        this.abierto = false;
      }
    },
    isActive(route) {
      if (!route) return false;
      return this.$route.path === route;
    },
    isAnyActive(routes) {
      if (!Array.isArray(routes)) return false;
      return routes.some(r => r && (this.$route.path === r || this.$route.path.startsWith(r + '/')));
    },
    handleClick() {
      if (!this.item) return;
      if (this.tieneHijos) {
        this.cancelarCierre();
        this.calcularAlineacion();
        this.abierto = !this.abierto;
      } else if (this.item.ruta) {
        this.$emit('item-click', this.item);
        if (/^https?:\/\//i.test(this.item.ruta)) {
          window.open(this.item.ruta, '_blank', 'noopener');
        } else {
          this.$router.push(this.item.ruta).catch(() => {});
        }
      }
    }
  }
};
</script>

<style scoped>
.custom-menu-scroll::-webkit-scrollbar {
  width: 5px;
}
.custom-menu-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.custom-menu-scroll::-webkit-scrollbar-thumb {
  background: rgba(99, 102, 241, 0.4);
  border-radius: 4px;
}
</style>
